import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { adminApi, formatApiError } from '../../lib/api';
import { getPlanLabel, PLAN_LABELS, PLAN_PRICES } from '../../lib/planLabels';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
    Users, Building, Briefcase, FileText, CreditCard, Search, Loader2,
    Shield, Eye, UserX, UserCheck, Trash2, ChevronDown, BarChart3, Activity,
    ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown, X, Filter
} from 'lucide-react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../../components/ui/tabs';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const ADMIN_PAGE_SIZE = 10;

// --- Sortable column header ---
const SortableHeader = ({ label, sortKey, sort, setSort, align = 'left', testId }) => {
    const active = sort.by === sortKey;
    const dir = active ? sort.order : null;
    const handleClick = () => {
        if (!active) setSort({ by: sortKey, order: 'asc' });
        else setSort({ by: sortKey, order: dir === 'asc' ? 'desc' : 'asc' });
    };
    const Icon = !active ? ArrowUpDown : (dir === 'asc' ? ArrowUp : ArrowDown);
    const ariaSort = !active ? 'none' : (dir === 'asc' ? 'ascending' : 'descending');
    return (
        <th
            className={`px-6 py-3 text-${align} text-xs font-semibold text-[#4B5563] uppercase tracking-wider`}
            aria-sort={ariaSort}
        >
            <button
                onClick={handleClick}
                className={`inline-flex items-center gap-1 select-none transition-colors ${active ? 'text-[#002FA7]' : 'text-[#4B5563] hover:text-[#0A0A0A]'}`}
                data-testid={testId}
                aria-sort={ariaSort}
            >
                {label}
                <Icon className="w-3.5 h-3.5" />
            </button>
        </th>
    );
};

export default function AdminPage() {
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState({ users: [], total: 0 });
    const [organizations, setOrganizations] = useState({ organizations: [], total: 0 });
    const [transactions, setTransactions] = useState({ transactions: [], total: 0 });
    const [orgsListAll, setOrgsListAll] = useState([]); // for filter dropdown
    const [loading, setLoading] = useState(true);

    // Search
    const [userSearch, setUserSearch] = useState('');
    const [orgSearch, setOrgSearch] = useState('');
    const [transSearch, setTransSearch] = useState('');

    // Sort state
    const [usersSort, setUsersSort] = useState({ by: 'created_at', order: 'desc' });
    const [orgsSort, setOrgsSort] = useState({ by: 'created_at', order: 'desc' });
    const [transSort, setTransSort] = useState({ by: 'created_at', order: 'desc' });

    // Filters
    const [userFilters, setUserFilters] = useState({ role: 'all', status: 'all', organization_id: 'all' });
    const [orgFilters, setOrgFilters] = useState({ subscription_plan: 'all', trial_status: 'all', quota_min: '', quota_max: '' });
    const [transFilters, setTransFilters] = useState({ payment_status: 'all', plan_id: 'all', date_from: '', date_to: '', amount_min: '', amount_max: '' });

    // Updating flags
    const [updatingRole, setUpdatingRole] = useState(null);
    const [updatingStatus, setUpdatingStatus] = useState(null);
    const [updatingPlan, setUpdatingPlan] = useState(null);
    const [deleteUser, setDeleteUser] = useState(null);
    const [orgDeleteTarget, setOrgDeleteTarget] = useState(null);
    const [orgDeleting, setOrgDeleting] = useState(false);

    // Plan-change confirmation dialog (Stripe-driven)
    const [planChangeDialog, setPlanChangeDialog] = useState(null); // { org, newPlan }
    const [planChangePreview, setPlanChangePreview] = useState(null); // { isUpgrade, isDowngradeToFree, ...}

    // Pagination
    const [usersPage, setUsersPage] = useState(1);
    const [orgsPage, setOrgsPage] = useState(1);
    const [transPage, setTransPage] = useState(1);

    const navigate = useNavigate();

    // -------- Initial load --------
    useEffect(() => {
        (async () => {
            try {
                const [statsRes, orgsListRes] = await Promise.all([
                    adminApi.getStats(),
                    adminApi.getOrganizationsList(),
                ]);
                setStats(statsRes.data);
                setOrgsListAll(orgsListRes.data?.organizations || []);
            } catch {
                toast.error(t('admin.load_error'));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // -------- Loaders (memoized to use in deps) --------
    const loadUsers = useCallback(async () => {
        try {
            const params = {
                skip: (usersPage - 1) * ADMIN_PAGE_SIZE,
                limit: ADMIN_PAGE_SIZE,
                sort_by: usersSort.by,
                sort_order: usersSort.order,
            };
            if (userSearch) params.search = userSearch;
            if (userFilters.role !== 'all') params.role = userFilters.role;
            if (userFilters.status !== 'all') params.status = userFilters.status;
            if (userFilters.organization_id !== 'all') params.organization_id = userFilters.organization_id;
            const res = await adminApi.getUsers(params);
            setUsers(res.data);
        } catch (error) {
            toast.error(formatApiError(error));
        }
    }, [usersPage, usersSort, userSearch, userFilters]);

    const loadOrgs = useCallback(async () => {
        try {
            const params = {
                skip: (orgsPage - 1) * ADMIN_PAGE_SIZE,
                limit: ADMIN_PAGE_SIZE,
                sort_by: orgsSort.by,
                sort_order: orgsSort.order,
            };
            if (orgSearch) params.search = orgSearch;
            if (orgFilters.subscription_plan !== 'all') params.subscription_plan = orgFilters.subscription_plan;
            if (orgFilters.trial_status !== 'all') params.trial_status = orgFilters.trial_status;
            if (orgFilters.quota_min !== '') params.quota_min = Number(orgFilters.quota_min);
            if (orgFilters.quota_max !== '') params.quota_max = Number(orgFilters.quota_max);
            const res = await adminApi.getOrganizations(params);
            setOrganizations(res.data);
        } catch (error) {
            toast.error(formatApiError(error));
        }
    }, [orgsPage, orgsSort, orgSearch, orgFilters]);

    const loadTrans = useCallback(async () => {
        try {
            const params = {
                skip: (transPage - 1) * ADMIN_PAGE_SIZE,
                limit: ADMIN_PAGE_SIZE,
                sort_by: transSort.by,
                sort_order: transSort.order,
            };
            if (transSearch) params.search = transSearch;
            if (transFilters.payment_status !== 'all') params.payment_status = transFilters.payment_status;
            if (transFilters.plan_id !== 'all') params.plan_id = transFilters.plan_id;
            if (transFilters.date_from) params.date_from = new Date(transFilters.date_from).toISOString();
            if (transFilters.date_to) params.date_to = new Date(transFilters.date_to + 'T23:59:59').toISOString();
            if (transFilters.amount_min !== '') params.amount_min = Number(transFilters.amount_min);
            if (transFilters.amount_max !== '') params.amount_max = Number(transFilters.amount_max);
            const res = await adminApi.getTransactions(params);
            setTransactions(res.data);
        } catch (error) {
            toast.error(formatApiError(error));
        }
    }, [transPage, transSort, transSearch, transFilters]);

    // Reset to page 1 when filters / sort / search change
    useEffect(() => { setUsersPage(1); }, [usersSort, userFilters, userSearch]);
    useEffect(() => { setOrgsPage(1); }, [orgsSort, orgFilters, orgSearch]);
    useEffect(() => { setTransPage(1); }, [transSort, transFilters, transSearch]);

    useEffect(() => { loadUsers(); }, [loadUsers]);
    useEffect(() => { loadOrgs(); }, [loadOrgs]);
    useEffect(() => { loadTrans(); }, [loadTrans]);

    // -------- Mutations --------
    const handleRoleChange = async (userId, newRole) => {
        setUpdatingRole(userId);
        try {
            await adminApi.updateUserRole(userId, newRole);
            toast.success(t('admin.role_updated'));
            await loadUsers();
        } catch (error) {
            toast.error(formatApiError(error));
        } finally {
            setUpdatingRole(null);
        }
    };

    const handleStatusToggle = async (userId, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
        setUpdatingStatus(userId);
        try {
            await adminApi.updateUserStatus(userId, newStatus);
            toast.success(newStatus === 'suspended' ? t('admin.user_suspended') : t('admin.user_reactivated'));
            await loadUsers();
            const statsRes = await adminApi.getStats();
            setStats(statsRes.data);
        } catch (error) {
            toast.error(formatApiError(error));
        } finally {
            setUpdatingStatus(null);
        }
    };

    const handleDeleteUser = async () => {
        if (!deleteUser) return;
        try {
            await adminApi.deleteUser(deleteUser.id);
            toast.success(t('admin.user_trashed'));
            setDeleteUser(null);
            await loadUsers();
            const statsRes = await adminApi.getStats();
            setStats(statsRes.data);
        } catch (error) {
            toast.error(formatApiError(error));
        }
    };

    const handlePlanChange = (orgId, newPlan) => {
        // Stripe-driven flow: show confirmation dialog with impact, no immediate write.
        const org = organizations.organizations.find(o => o.id === orgId);
        if (!org) return;
        if (org.subscription_plan === newPlan) return;

        const newPrice = PLAN_PRICES[newPlan] ?? 0;
        const currentPrice = PLAN_PRICES[org.subscription_plan] ?? 0;
        const isDowngradeToFree = newPlan === 'free';
        const isFreeToPaid = org.subscription_plan === 'free' && newPlan !== 'free';
        const isUpgrade = !isDowngradeToFree && !isFreeToPaid && (newPrice > currentPrice);
        const isPaidDowngrade = !isDowngradeToFree && !isFreeToPaid && (newPrice < currentPrice);

        setPlanChangePreview({
            isUpgrade,
            isDowngradeToFree,
            isFreeToPaid,
            isPaidDowngrade,
            currentLabel: getPlanLabel(org.subscription_plan),
            newLabel: getPlanLabel(newPlan),
            currentPrice,
            newPrice,
        });
        setPlanChangeDialog({ org, newPlan });
    };

    const confirmPlanChange = async () => {
        if (!planChangeDialog) return;
        const { org, newPlan } = planChangeDialog;
        setUpdatingPlan(org.id);
        try {
            if (planChangePreview?.isFreeToPaid) {
                // Generate Stripe checkout link admin can share
                const res = await adminApi.generateCheckoutLink(org.id, newPlan, window.location.origin);
                if (res.data?.url) {
                    await navigator.clipboard.writeText(res.data.url).catch(() => {});
                    toast.success(t('admin.stripe_link_generated'));
                }
            } else {
                const res = await adminApi.updateOrgPlan(org.id, newPlan);
                toast.success(res.data?.message || t('admin.stripe_change_applied'));
            }
            setPlanChangeDialog(null);
            setPlanChangePreview(null);
            await loadOrgs();
            const statsRes = await adminApi.getStats();
            setStats(statsRes.data);
        } catch (error) {
            toast.error(formatApiError(error));
        } finally {
            setUpdatingPlan(null);
        }
    };

    // -------- UI helpers --------
    const getStatusBadge = (status) => {
        if (status === 'suspended') {
            return <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#FEE2E2] text-[#DC2626]">{t('admin.status_suspended')}</span>;
        }
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#D1FAE5] text-[#059669]">{t('admin.status_active')}</span>;
    };

    const getPlanBadge = (plan) => {
        const label = getPlanLabel(plan);
        switch (plan) {
            case 'enterprise':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#DBEAFE] text-[#002FA7]">{label}</span>;
            case 'pro':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#D1FAE5] text-[#059669]">{label}</span>;
            default:
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#F3F4F6] text-[#4B5563]">{label}</span>;
        }
    };

    // Active filter counters
    const userActiveFilters = useMemo(() => {
        let n = 0;
        if (userFilters.role !== 'all') n++;
        if (userFilters.status !== 'all') n++;
        if (userFilters.organization_id !== 'all') n++;
        return n;
    }, [userFilters]);
    const orgActiveFilters = useMemo(() => {
        let n = 0;
        if (orgFilters.subscription_plan !== 'all') n++;
        if (orgFilters.trial_status !== 'all') n++;
        if (orgFilters.quota_min !== '') n++;
        if (orgFilters.quota_max !== '') n++;
        return n;
    }, [orgFilters]);
    const transActiveFilters = useMemo(() => {
        let n = 0;
        if (transFilters.payment_status !== 'all') n++;
        if (transFilters.plan_id !== 'all') n++;
        if (transFilters.date_from !== '') n++;
        if (transFilters.date_to !== '') n++;
        if (transFilters.amount_min !== '') n++;
        if (transFilters.amount_max !== '') n++;
        return n;
    }, [transFilters]);

    const resetUserFilters = () => {
        setUserFilters({ role: 'all', status: 'all', organization_id: 'all' });
        setUserSearch('');
    };
    const resetOrgFilters = () => {
        setOrgFilters({ subscription_plan: 'all', trial_status: 'all', quota_min: '', quota_max: '' });
        setOrgSearch('');
    };
    const resetTransFilters = () => {
        setTransFilters({ payment_status: 'all', plan_id: 'all', date_from: '', date_to: '', amount_min: '', amount_max: '' });
        setTransSearch('');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="admin-page">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#002FA7] rounded-lg flex items-center justify-center">
                    <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit']">{t('admin.page_title')}</h1>
                    <p className="text-[#4B5563]">{t('admin.subtitle')}</p>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <div className="bg-white p-4 rounded-lg border border-[#E5E7EB]" data-testid="stat-users">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#DBEAFE] rounded-lg flex items-center justify-center">
                            <Users className="w-5 h-5 text-[#002FA7]" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-[#0A0A0A]">{stats?.users || 0}</p>
                            <p className="text-xs text-[#9CA3AF]">{t('admin.stat_users')}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-lg border border-[#E5E7EB]" data-testid="stat-active">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#D1FAE5] rounded-lg flex items-center justify-center">
                            <UserCheck className="w-5 h-5 text-[#10B981]" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-[#0A0A0A]">{stats?.active_users || 0}</p>
                            <p className="text-xs text-[#9CA3AF]">{t('admin.stat_active')}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-lg border border-[#E5E7EB]" data-testid="stat-orgs">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#FEF3C7] rounded-lg flex items-center justify-center">
                            <Building className="w-5 h-5 text-[#F59E0B]" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-[#0A0A0A]">{stats?.organizations || 0}</p>
                            <p className="text-xs text-[#9CA3AF]">{t('admin.stat_orgs')}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-lg border border-[#E5E7EB]" data-testid="stat-jobs">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#E0E7FF] rounded-lg flex items-center justify-center">
                            <Briefcase className="w-5 h-5 text-[#6366F1]" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-[#0A0A0A]">{stats?.jobs || 0}</p>
                            <p className="text-xs text-[#9CA3AF]">{t('admin.stat_jobs')}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-lg border border-[#E5E7EB]" data-testid="stat-candidates">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#FEE2E2] rounded-lg flex items-center justify-center">
                            <FileText className="w-5 h-5 text-[#EF4444]" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-[#0A0A0A]">{stats?.candidates || 0}</p>
                            <p className="text-xs text-[#9CA3AF]">{t('admin.stat_candidates')}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-lg border border-[#E5E7EB]" data-testid="stat-analyzed">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#FCE7F3] rounded-lg flex items-center justify-center">
                            <BarChart3 className="w-5 h-5 text-[#EC4899]" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-[#0A0A0A]">{stats?.analyzed_candidates || 0}</p>
                            <p className="text-xs text-[#9CA3AF]">{t('admin.stat_analyses')}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Subscription Breakdown */}
            {stats?.subscriptions && (
                <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]">
                    <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-4">
                        {t('admin.subscriptions_breakdown')}
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="text-center p-4 bg-[#F3F4F6] rounded-lg">
                            <p className="text-2xl font-bold text-[#0A0A0A]">{stats.subscriptions.free || 0}</p>
                            <p className="text-sm text-[#9CA3AF] truncate">{PLAN_LABELS.free}</p>
                        </div>
                        <div className="text-center p-4 bg-[#D1FAE5] rounded-lg">
                            <p className="text-2xl font-bold text-[#059669]">{stats.subscriptions.pro || 0}</p>
                            <p className="text-sm text-[#059669] truncate">{PLAN_LABELS.pro}</p>
                        </div>
                        <div className="text-center p-4 bg-[#DBEAFE] rounded-lg">
                            <p className="text-2xl font-bold text-[#002FA7]">{stats.subscriptions.enterprise || 0}</p>
                            <p className="text-sm text-[#002FA7] truncate">{PLAN_LABELS.enterprise}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Tabs: full tab UI on md+, stacked sections on mobile */}
            <Tabs defaultValue="users" className="bg-white rounded-lg border border-[#E5E7EB]">
                <TabsList className="hidden md:flex w-full border-b border-[#E5E7EB] rounded-none bg-transparent p-0 overflow-x-auto flex-nowrap">
                    <TabsTrigger
                        value="users"
                        className="flex-1 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:text-[#002FA7]"
                        data-testid="tab-users"
                    >
                        <Users className="w-4 h-4 mr-2" />
                        {t('admin.tab_users')} ({users.total})
                    </TabsTrigger>
                    <TabsTrigger
                        value="subscriptions"
                        className="flex-1 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:text-[#002FA7]"
                        data-testid="tab-subscriptions"
                    >
                        <CreditCard className="w-4 h-4 mr-2" />
                        {t('admin.tab_subscriptions')} ({organizations.total})
                    </TabsTrigger>
                    <TabsTrigger
                        value="transactions"
                        className="flex-1 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:text-[#002FA7]"
                        data-testid="tab-transactions"
                    >
                        <Activity className="w-4 h-4 mr-2" />
                        {t('admin.tab_transactions')} ({transactions.total})
                    </TabsTrigger>
                </TabsList>

                {/* Mobile-only section header for USERS */}
                <div className="md:hidden flex items-center gap-2 px-4 py-3 bg-[#F9FAFB] border-b border-[#E5E7EB]" data-testid="mobile-section-users">
                    <Users className="w-4 h-4 text-[#002FA7]" />
                    <h2 className="text-sm font-semibold text-[#0A0A0A]">
                        {t('admin.tab_users')} ({users.total})
                    </h2>
                </div>

                {/* ========================== USERS TAB ========================== */}
                <TabsContent value="users" className="p-0 data-[state=inactive]:md:hidden" forceMount>
                    <div className="p-4 border-b border-[#E5E7EB] space-y-3">
                        {/* Search row */}
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                                <Input
                                    placeholder={t('admin.search_users')}
                                    value={userSearch}
                                    onChange={(e) => setUserSearch(e.target.value)}
                                    className="pl-10"
                                    data-testid="admin-user-search"
                                />
                            </div>
                            {userActiveFilters > 0 && (
                                <Button variant="ghost" onClick={resetUserFilters} data-testid="users-reset-filters" title={t('admin.reset_filters')}>
                                    <X className="w-4 h-4 mr-1" />
                                    {t('admin.reset_filters')} ({userActiveFilters})
                                </Button>
                            )}
                        </div>
                        {/* Filter row */}
                        <div className="flex flex-wrap gap-2">
                            <div className="flex items-center gap-1.5 text-xs text-[#9CA3AF]">
                                <Filter className="w-3.5 h-3.5" />
                                {t('admin.filters')}
                            </div>
                            <Select value={userFilters.role} onValueChange={(v) => setUserFilters(p => ({ ...p, role: v }))}>
                                <SelectTrigger className="w-full sm:w-[160px] h-8 text-xs" data-testid="users-filter-role">
                                    <SelectValue placeholder={t('admin.col_role')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('admin.all_roles')}</SelectItem>
                                    <SelectItem value="admin">{t('admin.role_admin')}</SelectItem>
                                    <SelectItem value="manager">{t('admin.role_manager')}</SelectItem>
                                    <SelectItem value="recruiter">{t('admin.role_recruiter')}</SelectItem>
                                    <SelectItem value="billing_admin">{t('admin.role_billing')}</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={userFilters.status} onValueChange={(v) => setUserFilters(p => ({ ...p, status: v }))}>
                                <SelectTrigger className="w-full sm:w-[160px] h-8 text-xs" data-testid="users-filter-status">
                                    <SelectValue placeholder={t('admin.col_status')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('admin.all_statuses')}</SelectItem>
                                    <SelectItem value="active">{t('admin.status_active')}</SelectItem>
                                    <SelectItem value="suspended">{t('admin.status_suspended')}</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={userFilters.organization_id} onValueChange={(v) => setUserFilters(p => ({ ...p, organization_id: v }))}>
                                <SelectTrigger className="w-full sm:w-[220px] h-8 text-xs" data-testid="users-filter-org">
                                    <SelectValue placeholder={t('admin.company_placeholder')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('admin.all_orgs')}</SelectItem>
                                    {orgsListAll.map((o) => (
                                        <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                                <tr>
                                    <SortableHeader label={t('admin.col_user')} sortKey="name" sort={usersSort} setSort={setUsersSort} testId="sort-users-name" />
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase hidden md:table-cell">{t('admin.col_company')}</th>
                                    <SortableHeader label={t('admin.col_role')} sortKey="role" sort={usersSort} setSort={setUsersSort} testId="sort-users-role" />
                                    <SortableHeader label={t('admin.col_status')} sortKey="status" sort={usersSort} setSort={setUsersSort} testId="sort-users-status" />
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase hidden lg:table-cell">{t('admin.col_plan')}</th>
                                    <SortableHeader label={t('admin.col_date')} sortKey="created_at" sort={usersSort} setSort={setUsersSort} testId="sort-users-date" />
                                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#4B5563] uppercase">{t('admin.col_actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#E5E7EB]">
                                {users.users.map((user) => (
                                    <tr key={user.id} className="hover:bg-[#F9FAFB]" data-testid={`user-row-${user.id}`}>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-full bg-[#002FA7] flex items-center justify-center text-white text-sm font-medium">
                                                    {user.name?.charAt(0)?.toUpperCase() || '?'}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-[#0A0A0A] text-sm">{user.name}</p>
                                                    <p className="text-xs text-[#9CA3AF]">{user.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-[#4B5563] hidden md:table-cell">
                                            {user.organization_name || '-'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <Select
                                                value={user.role}
                                                onValueChange={(value) => handleRoleChange(user.id, value)}
                                                disabled={updatingRole === user.id}
                                            >
                                                <SelectTrigger className="w-[130px] h-8 text-xs">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="admin">{t('admin.role_admin')}</SelectItem>
                                                    <SelectItem value="manager">{t('admin.role_manager')}</SelectItem>
                                                    <SelectItem value="recruiter">{t('admin.role_recruiter')}</SelectItem>
                                                    <SelectItem value="billing_admin">{t('admin.role_billing')}</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </td>
                                        <td className="px-6 py-4">
                                            {getStatusBadge(user.status)}
                                        </td>
                                        <td className="px-6 py-4 hidden lg:table-cell">
                                            {getPlanBadge(user.subscription_plan)}
                                        </td>
                                        <td className="px-6 py-4 text-[#9CA3AF] text-xs">
                                            {user.created_at ? new Date(user.created_at).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'fr-FR') : '-'}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button size="sm" variant="ghost" data-testid={`user-actions-${user.id}`}>
                                                        <ChevronDown className="w-4 h-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem onClick={() => navigate(`/dashboard/admin/users/${user.id}`)}>
                                                        <Eye className="w-4 h-4 mr-2" />
                                                        {t('admin.view_detail')}
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        onClick={() => handleStatusToggle(user.id, user.status)}
                                                        disabled={updatingStatus === user.id}
                                                    >
                                                        {user.status === 'active' ? (
                                                            <><UserX className="w-4 h-4 mr-2" />{t('admin.suspend')}</>
                                                        ) : (
                                                            <><UserCheck className="w-4 h-4 mr-2" />{t('admin.reactivate')}</>
                                                        )}
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        onClick={() => setDeleteUser(user)}
                                                        className="text-[#EF4444] focus:text-[#EF4444]"
                                                    >
                                                        <Trash2 className="w-4 h-4 mr-2" />
                                                        {t('admin.delete')}
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </td>
                                    </tr>
                                ))}
                                {users.users.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-12 text-center text-[#9CA3AF]">
                                            {t('admin.no_users')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {(() => {
                        const totalPages = Math.max(1, Math.ceil(users.total / ADMIN_PAGE_SIZE));
                        return totalPages > 1 ? (
                            <div className="flex items-center justify-between px-6 py-3 border-t border-[#E5E7EB]" data-testid="admin-users-pagination">
                                <p className="text-sm text-[#9CA3AF]">{users.total <= 1 ? t('admin.users_count_one', { count: users.total }) : t('admin.users_count_other', { count: users.total })}</p>
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" onClick={() => setUsersPage(p => p - 1)} disabled={usersPage <= 1} data-testid="admin-users-prev"><ChevronLeft className="w-4 h-4" /></Button>
                                    <span className="text-sm text-[#4B5563] min-w-[80px] text-center">{usersPage} / {totalPages}</span>
                                    <Button variant="outline" size="sm" onClick={() => setUsersPage(p => p + 1)} disabled={usersPage >= totalPages} data-testid="admin-users-next"><ChevronRight className="w-4 h-4" /></Button>
                                </div>
                            </div>
                        ) : null;
                    })()}
                </TabsContent>

                {/* ========================== SUBSCRIPTIONS TAB ========================== */}
                <TabsContent value="subscriptions" className="p-0 data-[state=inactive]:md:hidden" forceMount>
                    {/* Mobile-only section header for SUBSCRIPTIONS */}
                    <div className="md:hidden flex items-center gap-2 px-4 py-3 bg-[#F9FAFB] border-t border-b border-[#E5E7EB]" data-testid="mobile-section-subscriptions">
                        <CreditCard className="w-4 h-4 text-[#002FA7]" />
                        <h2 className="text-sm font-semibold text-[#0A0A0A]">
                            {t('admin.tab_subscriptions')} ({organizations.total})
                        </h2>
                    </div>
                    <div className="p-4 border-b border-[#E5E7EB] space-y-3">
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                                <Input
                                    placeholder={t('admin.search_orgs')}
                                    value={orgSearch}
                                    onChange={(e) => setOrgSearch(e.target.value)}
                                    className="pl-10"
                                    data-testid="admin-org-search"
                                />
                            </div>
                            {orgActiveFilters > 0 && (
                                <Button variant="ghost" onClick={resetOrgFilters} data-testid="orgs-reset-filters" title={t('admin.reset_filters')}>
                                    <X className="w-4 h-4 mr-1" />
                                    {t('admin.reset_filters')} ({orgActiveFilters})
                                </Button>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2 items-center">
                            <div className="flex items-center gap-1.5 text-xs text-[#9CA3AF]">
                                <Filter className="w-3.5 h-3.5" />
                                {t('admin.filters')}
                            </div>
                            <Select value={orgFilters.subscription_plan} onValueChange={(v) => setOrgFilters(p => ({ ...p, subscription_plan: v }))}>
                                <SelectTrigger className="w-full sm:w-[150px] h-8 text-xs" data-testid="orgs-filter-plan">
                                    <SelectValue placeholder={t('admin.type_placeholder')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('admin.all_plans')}</SelectItem>
                                    <SelectItem value="free">{PLAN_LABELS.free}</SelectItem>
                                    <SelectItem value="pro">{PLAN_LABELS.pro}</SelectItem>
                                    <SelectItem value="enterprise">{PLAN_LABELS.enterprise}</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={orgFilters.trial_status} onValueChange={(v) => setOrgFilters(p => ({ ...p, trial_status: v }))}>
                                <SelectTrigger className="w-full sm:w-[180px] h-8 text-xs" data-testid="orgs-filter-trial">
                                    <SelectValue placeholder={t('admin.status_placeholder')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('admin.all_statuses')}</SelectItem>
                                    <SelectItem value="active">{t('admin.status_active')}</SelectItem>
                                    <SelectItem value="exhausted">{t('admin.status_exhausted')}</SelectItem>
                                </SelectContent>
                            </Select>
                            <div className="flex items-center gap-1">
                                <span className="text-xs text-[#9CA3AF]">{t('admin.quota_pct')}</span>
                                <Input
                                    type="number"
                                    min="0"
                                    max="100"
                                    placeholder={t('admin.min_placeholder')}
                                    value={orgFilters.quota_min}
                                    onChange={(e) => setOrgFilters(p => ({ ...p, quota_min: e.target.value }))}
                                    className="w-[70px] h-8 text-xs"
                                    data-testid="orgs-filter-quota-min"
                                />
                                <span className="text-xs text-[#9CA3AF]">-</span>
                                <Input
                                    type="number"
                                    min="0"
                                    max="100"
                                    placeholder={t('admin.max_placeholder')}
                                    value={orgFilters.quota_max}
                                    onChange={(e) => setOrgFilters(p => ({ ...p, quota_max: e.target.value }))}
                                    className="w-[70px] h-8 text-xs"
                                    data-testid="orgs-filter-quota-max"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                                <tr>
                                    <SortableHeader label={t('admin.col_company')} sortKey="name" sort={orgsSort} setSort={setOrgsSort} testId="sort-orgs-name" />
                                    <SortableHeader label={t('admin.col_plan')} sortKey="subscription_plan" sort={orgsSort} setSort={setOrgsSort} testId="sort-orgs-plan" />
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase hidden md:table-cell">{t('admin.col_members')}</th>
                                    <SortableHeader label={t('admin.col_usage')} sortKey="analysis_count" sort={orgsSort} setSort={setOrgsSort} testId="sort-orgs-usage" />
                                    <SortableHeader label={t('admin.col_date')} sortKey="created_at" sort={orgsSort} setSort={setOrgsSort} testId="sort-orgs-date" />
                                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#4B5563] uppercase">{t('admin.col_modify')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#E5E7EB]">
                                {organizations.organizations.map((org) => {
                                    const planQuotas = { free: 5, pro: 100, enterprise: 1000 };
                                    const maxCvs = planQuotas[org.subscription_plan] || 5;
                                    const analysisCount = org.analysis_count || org.candidates_analyzed || 0;
                                    const usagePercent = org.usage_percent !== undefined ? org.usage_percent : (maxCvs > 0 ? Math.round(analysisCount / maxCvs * 100) : 0);
                                    return (
                                        <tr key={org.id} className="hover:bg-[#F9FAFB]" data-testid={`org-row-${org.id}`}>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 rounded-lg bg-[#F3F4F6] flex items-center justify-center">
                                                        <Building className="w-4 h-4 text-[#4B5563]" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-[#0A0A0A] text-sm">{org.name}</p>
                                                        <p className="text-xs text-[#9CA3AF]">{org.industry || t('admin.industry_unspecified')}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                {getPlanBadge(org.subscription_plan)}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-[#4B5563] hidden md:table-cell">
                                                {org.members_count || 0}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-2 text-xs">
                                                        <span className="text-[#4B5563]">
                                                            {analysisCount} / {maxCvs}
                                                        </span>
                                                        <span className={`font-medium ${usagePercent > 80 ? 'text-[#EF4444]' : 'text-[#9CA3AF]'}`}>
                                                            ({usagePercent}%)
                                                        </span>
                                                    </div>
                                                    <div className="w-24 h-1.5 bg-[#E5E7EB] rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full rounded-full ${usagePercent > 80 ? 'bg-[#EF4444]' : usagePercent > 50 ? 'bg-[#F59E0B]' : 'bg-[#10B981]'}`}
                                                            style={{ width: `${Math.min(usagePercent, 100)}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-[#9CA3AF] text-xs">
                                                {org.created_at ? new Date(org.created_at).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'fr-FR') : '-'}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <Select
                                                    value={org.subscription_plan}
                                                    onValueChange={(value) => handlePlanChange(org.id, value)}
                                                    disabled={updatingPlan === org.id}
                                                >
                                                    <SelectTrigger className="w-[130px] h-8 text-xs" data-testid={`plan-select-${org.id}`}>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem
                                                            value="free"
                                                            disabled={org.free_trial_used && org.subscription_plan !== 'free'}
                                                            data-testid={`plan-option-free-${org.id}`}
                                                        >
                                                            {PLAN_LABELS.free}{org.free_trial_used && org.subscription_plan !== 'free' ? t('admin.trial_used_suffix') : ''}
                                                        </SelectItem>
                                                        <SelectItem value="pro">{PLAN_LABELS.pro}</SelectItem>
                                                        <SelectItem value="enterprise">{PLAN_LABELS.enterprise}</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setOrgDeleteTarget(org)}
                                                    className="ml-2 text-[#DC2626] hover:text-[#B91C1C] hover:bg-[#FEE2E2] inline-flex"
                                                    data-testid={`soft-delete-org-btn-${org.id}`}
                                                    title="Supprimer cette organisation"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {organizations.organizations.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-12 text-center text-[#9CA3AF]">
                                            {t('admin.no_orgs')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {(() => {
                        const totalPages = Math.max(1, Math.ceil(organizations.total / ADMIN_PAGE_SIZE));
                        return totalPages > 1 ? (
                            <div className="flex items-center justify-between px-6 py-3 border-t border-[#E5E7EB]" data-testid="admin-orgs-pagination">
                                <p className="text-sm text-[#9CA3AF]">{organizations.total <= 1 ? t('admin.orgs_count_one', { count: organizations.total }) : t('admin.orgs_count_other', { count: organizations.total })}</p>
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" onClick={() => setOrgsPage(p => p - 1)} disabled={orgsPage <= 1} data-testid="admin-orgs-prev"><ChevronLeft className="w-4 h-4" /></Button>
                                    <span className="text-sm text-[#4B5563] min-w-[80px] text-center">{orgsPage} / {totalPages}</span>
                                    <Button variant="outline" size="sm" onClick={() => setOrgsPage(p => p + 1)} disabled={orgsPage >= totalPages} data-testid="admin-orgs-next"><ChevronRight className="w-4 h-4" /></Button>
                                </div>
                            </div>
                        ) : null;
                    })()}
                </TabsContent>

                {/* ========================== TRANSACTIONS TAB ========================== */}
                <TabsContent value="transactions" className="p-0 data-[state=inactive]:md:hidden" forceMount>
                    {/* Mobile-only section header for TRANSACTIONS */}
                    <div className="md:hidden flex items-center gap-2 px-4 py-3 bg-[#F9FAFB] border-t border-b border-[#E5E7EB]" data-testid="mobile-section-transactions">
                        <Activity className="w-4 h-4 text-[#002FA7]" />
                        <h2 className="text-sm font-semibold text-[#0A0A0A]">
                            {t('admin.tab_transactions')} ({transactions.total})
                        </h2>
                    </div>
                    <div className="p-4 border-b border-[#E5E7EB] space-y-3">
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                                <Input
                                    placeholder={t('admin.search_trans')}
                                    value={transSearch}
                                    onChange={(e) => setTransSearch(e.target.value)}
                                    className="pl-10"
                                    data-testid="admin-trans-search"
                                />
                            </div>
                            {transActiveFilters > 0 && (
                                <Button variant="ghost" onClick={resetTransFilters} data-testid="trans-reset-filters" title={t('admin.reset_filters')}>
                                    <X className="w-4 h-4 mr-1" />
                                    {t('admin.reset_filters')} ({transActiveFilters})
                                </Button>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2 items-center">
                            <div className="flex items-center gap-1.5 text-xs text-[#9CA3AF]">
                                <Filter className="w-3.5 h-3.5" />
                                {t('admin.filters')}
                            </div>
                            <Select value={transFilters.payment_status} onValueChange={(v) => setTransFilters(p => ({ ...p, payment_status: v }))}>
                                <SelectTrigger className="w-full sm:w-[150px] h-8 text-xs" data-testid="trans-filter-status">
                                    <SelectValue placeholder={t('admin.status_placeholder')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('admin.all_statuses')}</SelectItem>
                                    <SelectItem value="paid">{t('admin.tx_succeeded')}</SelectItem>
                                    <SelectItem value="pending">{t('admin.tx_pending')}</SelectItem>
                                    <SelectItem value="failed">{t('admin.tx_failed')}</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={transFilters.plan_id} onValueChange={(v) => setTransFilters(p => ({ ...p, plan_id: v }))}>
                                <SelectTrigger className="w-full sm:w-[140px] h-8 text-xs" data-testid="trans-filter-plan">
                                    <SelectValue placeholder={t('admin.subscription_placeholder')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('admin.all_plans')}</SelectItem>
                                    <SelectItem value="pro">{PLAN_LABELS.pro}</SelectItem>
                                    <SelectItem value="enterprise">{PLAN_LABELS.enterprise}</SelectItem>
                                </SelectContent>
                            </Select>
                            <div className="flex items-center gap-1">
                                <span className="text-xs text-[#9CA3AF]">{t('admin.from_label')}</span>
                                <Input
                                    type="date"
                                    value={transFilters.date_from}
                                    onChange={(e) => setTransFilters(p => ({ ...p, date_from: e.target.value }))}
                                    className="w-[140px] h-8 text-xs"
                                    data-testid="trans-filter-date-from"
                                />
                                <span className="text-xs text-[#9CA3AF]">{t('admin.to_label')}</span>
                                <Input
                                    type="date"
                                    value={transFilters.date_to}
                                    onChange={(e) => setTransFilters(p => ({ ...p, date_to: e.target.value }))}
                                    className="w-[140px] h-8 text-xs"
                                    data-testid="trans-filter-date-to"
                                />
                            </div>
                            <div className="flex items-center gap-1">
                                <span className="text-xs text-[#9CA3AF]">{t('admin.amount_label')}</span>
                                <Input
                                    type="number"
                                    min="0"
                                    placeholder={t('admin.min_placeholder')}
                                    value={transFilters.amount_min}
                                    onChange={(e) => setTransFilters(p => ({ ...p, amount_min: e.target.value }))}
                                    className="w-[80px] h-8 text-xs"
                                    data-testid="trans-filter-amount-min"
                                />
                                <span className="text-xs text-[#9CA3AF]">-</span>
                                <Input
                                    type="number"
                                    min="0"
                                    placeholder={t('admin.max_placeholder')}
                                    value={transFilters.amount_max}
                                    onChange={(e) => setTransFilters(p => ({ ...p, amount_max: e.target.value }))}
                                    className="w-[80px] h-8 text-xs"
                                    data-testid="trans-filter-amount-max"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase">{t('admin.col_company')}</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase">{t('admin.col_session')}</th>
                                    <SortableHeader label={t('admin.col_plan')} sortKey="plan_id" sort={transSort} setSort={setTransSort} testId="sort-trans-plan" />
                                    <SortableHeader label={t('admin.col_amount')} sortKey="amount" sort={transSort} setSort={setTransSort} testId="sort-trans-amount" />
                                    <SortableHeader label={t('admin.col_status')} sortKey="payment_status" sort={transSort} setSort={setTransSort} testId="sort-trans-status" />
                                    <SortableHeader label={t('admin.col_date')} sortKey="created_at" sort={transSort} setSort={setTransSort} testId="sort-trans-date" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#E5E7EB]">
                                {transactions.transactions.length > 0 ? (
                                    transactions.transactions.map((tx) => (
                                        <tr key={tx.id || tx.session_id} className="hover:bg-[#F9FAFB]">
                                            <td className="px-6 py-4 text-[#0A0A0A] text-sm font-medium" data-testid={`trans-org-${tx.id || tx.session_id}`}>
                                                {tx.organization_name || '-'}
                                            </td>
                                            <td className="px-6 py-4 font-mono text-xs text-[#4B5563]">
                                                {tx.session_id?.slice(0, 20)}...
                                            </td>
                                            <td className="px-6 py-4 text-[#0A0A0A] text-sm">{getPlanLabel(tx.plan_id)}</td>
                                            <td className="px-6 py-4 font-medium text-[#0A0A0A] text-sm">
                                                {tx.amount} {tx.currency?.toUpperCase()}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                    tx.payment_status === 'paid' ? 'bg-[#D1FAE5] text-[#059669]' :
                                                    tx.payment_status === 'pending' ? 'bg-[#FEF3C7] text-[#D97706]' :
                                                    'bg-[#FEE2E2] text-[#DC2626]'
                                                }`}>
                                                    {tx.payment_status === 'paid' ? t('admin.tx_paid') : tx.payment_status === 'pending' ? t('admin.tx_pending') : tx.payment_status === 'failed' ? t('admin.tx_failed') : tx.payment_status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-[#9CA3AF] text-xs">
                                                {tx.created_at ? new Date(tx.created_at).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'fr-FR') : '-'}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-12 text-center text-[#9CA3AF]">
                                            {t('admin.no_trans')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {(() => {
                        const totalPages = Math.max(1, Math.ceil(transactions.total / ADMIN_PAGE_SIZE));
                        return totalPages > 1 ? (
                            <div className="flex items-center justify-between px-6 py-3 border-t border-[#E5E7EB]" data-testid="admin-trans-pagination">
                                <p className="text-sm text-[#9CA3AF]">{transactions.total <= 1 ? t('admin.trans_count_one', { count: transactions.total }) : t('admin.trans_count_other', { count: transactions.total })}</p>
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" onClick={() => setTransPage(p => p - 1)} disabled={transPage <= 1} data-testid="admin-trans-prev"><ChevronLeft className="w-4 h-4" /></Button>
                                    <span className="text-sm text-[#4B5563] min-w-[80px] text-center">{transPage} / {totalPages}</span>
                                    <Button variant="outline" size="sm" onClick={() => setTransPage(p => p + 1)} disabled={transPage >= totalPages} data-testid="admin-trans-next"><ChevronRight className="w-4 h-4" /></Button>
                                </div>
                            </div>
                        ) : transactions.total > 0 ? (
                            <div className="flex items-center justify-between px-6 py-3 border-t border-[#E5E7EB]" data-testid="admin-trans-pagination">
                                <p className="text-sm text-[#9CA3AF]">{transactions.total <= 1 ? t('admin.trans_count_one', { count: transactions.total }) : t('admin.trans_count_other', { count: transactions.total })}</p>
                                <span className="text-sm text-[#9CA3AF]">{t('admin.single_page_label')}</span>
                            </div>
                        ) : null;
                    })()}
                </TabsContent>
            </Tabs>

            {/* Delete User Confirmation */}
            <AlertDialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('admin.delete_user_title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('admin.delete_user_desc', { name: deleteUser?.name, email: deleteUser?.email })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteUser}
                            className="bg-[#EF4444] hover:bg-[#DC2626] text-white"
                            data-testid="confirm-delete-user-btn"
                        >
                            {t('common.delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Soft Delete Organization Confirmation */}
            <AlertDialog open={!!orgDeleteTarget} onOpenChange={(o) => { if (!o) setOrgDeleteTarget(null); }}>
                <AlertDialogContent data-testid="soft-delete-org-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer cette organisation ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Cette organisation sera désactivée et déplacée dans la corbeille.
                            Ses utilisateurs, offres, candidats et autres données associées ne seront pas supprimés
                            définitivement. Ils seront conservés et pourront être restaurés ultérieurement.
                            <br /><br />
                            <strong>Organisation : {orgDeleteTarget?.name}</strong>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={orgDeleting}>Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={async () => {
                                if (!orgDeleteTarget) return;
                                setOrgDeleting(true);
                                try {
                                    await adminApi.softDeleteOrganization(orgDeleteTarget.id);
                                    toast.success('Organisation déplacée dans la corbeille.');
                                    setOrgDeleteTarget(null);
                                    await loadOrgs();
                                } catch (e) {
                                    toast.error(formatApiError(e));
                                } finally {
                                    setOrgDeleting(false);
                                }
                            }}
                            disabled={orgDeleting}
                            className="bg-[#EF4444] hover:bg-[#DC2626] text-white"
                            data-testid="confirm-soft-delete-org-btn"
                        >
                            {orgDeleting ? 'Suppression…' : "Supprimer l'organisation"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Stripe-driven Plan Change Confirmation */}
            <AlertDialog
                open={!!planChangeDialog}
                onOpenChange={(open) => { if (!open) { setPlanChangeDialog(null); setPlanChangePreview(null); } }}
            >
                <AlertDialogContent data-testid="plan-change-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {t('admin.plan_change_title', { name: planChangeDialog?.org?.name })}
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3 text-sm text-[#4B5563]">
                                <div className="flex items-center justify-between bg-[#F3F4F6] rounded-lg px-3 py-2">
                                    <span>{planChangePreview?.currentLabel} ({planChangePreview?.currentPrice}€)</span>
                                    <span className="text-[#9CA3AF]">→</span>
                                    <span className="font-semibold text-[#0A0A0A]">
                                        {planChangePreview?.newLabel} ({planChangePreview?.newPrice}€)
                                    </span>
                                </div>

                                {planChangePreview?.isUpgrade && (
                                    <div className="bg-[#DBEAFE] border border-[#3B82F6]/30 rounded-lg p-3 text-[#1E3A8A]">
                                        {t('admin.plan_change_upgrade')}
                                    </div>
                                )}
                                {planChangePreview?.isPaidDowngrade && (
                                    <div className="bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-lg p-3 text-[#92400E]">
                                        {t('admin.plan_change_downgrade', { label: planChangePreview?.currentLabel })}
                                    </div>
                                )}
                                {planChangePreview?.isDowngradeToFree && (
                                    <div className="bg-[#FEE2E2] border border-[#EF4444]/30 rounded-lg p-3 text-[#7F1D1D]">
                                        {t('admin.plan_change_to_free')}
                                    </div>
                                )}
                                {planChangePreview?.isFreeToPaid && (
                                    <div className="bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-lg p-3 text-[#92400E]">
                                        {t('admin.plan_change_free_to_paid')}
                                    </div>
                                )}
                                <p className="text-xs text-[#9CA3AF]">
                                    {t('admin.plan_change_source_of_truth')}
                                </p>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="cancel-plan-change-btn">{t('common.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmPlanChange}
                            disabled={updatingPlan === planChangeDialog?.org?.id}
                            className="bg-[#002FA7] hover:bg-[#002480] text-white"
                            data-testid="confirm-plan-change-btn"
                        >
                            {updatingPlan === planChangeDialog?.org?.id ? (
                                <span className="flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Stripe…
                                </span>
                            ) : planChangePreview?.isFreeToPaid ? (
                                t('admin.plan_change_action_link')
                            ) : (
                                t('admin.plan_change_action_confirm')
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
